import React, { useState, useEffect, useMemo, useRef } from "react";
import AddImage from "../../assets/images/AddImage.jpg";
import search from "../../assets/svg/search.svg";
import Header from "../../components/Header/Header";
import ScrollToTop from "../../components/Hooks/ScrollToTop";
import SmallHeader from "../../components/Small_Header/SmallHeader";
import styles from "./Explore.module.scss";
import ExploreYourMind from "./ExploreYourMind";
import ExploreYourWorld from "./ExploreYourWorld";
import ExplorePostCard from "./ExplorePostCard";
import { extractPostMedia } from "./exploreUtils";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa6";
import axios from "../../components/Hooks/axios";
import { setCookies } from "../../components/Hooks/setCookies";
import Avatar from "../../components/Avatar/Avatar";

const TAGS = ["Travel", "Funny", "Photography", "News", "Pets", "RoadTrip"];

const Explore = () => {
	const { userId, token } = useSelector((state) => state.auth);
	const [userData, setUserData] = useState([]);
	const [posts, setPosts] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [selectedTag, setSelectedTag] = useState(null);
	const [searchQuery, setSearchQuery] = useState("");
	const [isSearching, setIsSearching] = useState(false);
	const [isMobile, setIsMobile] = useState(window.innerWidth < 431);
	const [showSearchRight, setShowSearchRight] = useState(true);
	const navigate = useNavigate();

	// Ref & states for user circles horizontal slider
	const userRowRef = useRef(null);
	const [canScrollUserLeft, setCanScrollUserLeft] = useState(false);
	const [canScrollUserRight, setCanScrollUserRight] = useState(false);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 431);
			checkUserScroll();
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const checkUserScroll = () => {
		if (!userRowRef.current) return;
		const { scrollLeft, scrollWidth, clientWidth } = userRowRef.current;
		setCanScrollUserLeft(scrollLeft > 5);
		setCanScrollUserRight(scrollLeft < scrollWidth - clientWidth - 5);
	};

	const scrollUsers = (direction) => {
		if (!userRowRef.current) return;
		const scrollAmount = Math.max(userRowRef.current.clientWidth * 0.7, 240);
		userRowRef.current.scrollBy({
			left: direction === "left" ? -scrollAmount : scrollAmount,
			behavior: "smooth",
		});
	};

	useEffect(() => {
		const timer = setTimeout(checkUserScroll, 150);
		return () => clearTimeout(timer);
	}, [userData]);

	const fetchInitialData = () => {
		setIsLoading(true);
		axios
			.get("/post/following?skip=0&limit=50")
			.then(({ data }) => {
				const allPosts = data?.AllPost || [];
				// Keep all valid posts (images, videos, multi-media)
				const validPosts = allPosts.filter((post) => {
					if (!post) return false;
					const { url } = extractPostMedia(post);
					return url && !url.includes("undefined");
				});
				setPosts(validPosts);
				setIsLoading(false);
			})
			.catch((err) => {
				console.error("Error fetching explore posts:", err);
				setIsLoading(false);
			});

		axios
			.get(`/auth/randomUser`)
			.then(({ data }) => {
				setUserData(data || []);
			})
			.catch((error) => {
				console.error("Error fetching random users:", error);
			});
	};

	useEffect(() => {
		fetchInitialData();
	}, []);

	const performSearch = () => {
		const query = searchQuery.trim();
		if (query !== "") {
			setIsSearching(true);
			setIsLoading(true);
			// Search for users
			axios
				.get(`/auth/userSearch?search=${encodeURIComponent(query)}`)
				.then(({ data }) => {
					const foundUsers = data || [];

					if (foundUsers.length > 0) {
						// Prioritize found users in the avatar row
						setUserData((prevUsers) => {
							const otherUsers = prevUsers.filter(
								(u) => !foundUsers.some((fu) => fu._id === u._id)
							);
							return [...foundUsers, ...otherUsers];
						});

						// Get posts for the first found user
						const searchedUserId = foundUsers[0]._id;
						axios
							.get(`/post/${searchedUserId}`)
							.then(({ data: postData }) => {
								const userPosts = Array.isArray(postData)
									? postData
									: postData?.posts || [];

								const validUserPosts = userPosts.filter((post) => {
									if (!post) return false;
									const { url } = extractPostMedia(post);
									return url && !url.includes("undefined");
								});

								// Prepend searched user posts to feed
								setPosts((prevPosts) => {
									const filteredPrev = prevPosts.filter(
										(p) => !validUserPosts.some((up) => up._id === p._id)
									);
									return [...validUserPosts, ...filteredPrev];
								});
								setIsLoading(false);
							})
							.catch((err) => {
								console.error("Error fetching user posts:", err);
								setIsLoading(false);
							});
					} else {
						// Filter existing posts by search keyword in caption or username
						setIsLoading(false);
					}
				})
				.catch((err) => {
					console.error("Error searching users:", err);
					setIsLoading(false);
				});
		} else {
			setIsSearching(false);
			fetchInitialData();
		}
	};

	const handleSearch = (e) => {
		if (e.key === "Enter") {
			performSearch();
		}
	};

	const handleTagToggle = (tag) => {
		setSelectedTag((prev) => (prev === tag ? null : tag));
	};

	// Filter posts by selected tag and active search query
	const displayedPosts = useMemo(() => {
		let result = posts;

		if (selectedTag) {
			const tagLower = selectedTag.toLowerCase();
			result = result.filter((post) => {
				const tags = Array.isArray(post.tags) ? post.tags : [];
				const matchesTags = tags.some(
					(t) => typeof t === "string" && t.toLowerCase().includes(tagLower)
				);
				const matchesCaption =
					typeof post.caption === "string" &&
					post.caption.toLowerCase().includes(tagLower);
				return matchesTags || matchesCaption;
			});
		}

		if (isSearching && searchQuery.trim() !== "") {
			const q = searchQuery.trim().toLowerCase();
			const keywordMatches = result.filter((post) => {
				const caption = post.caption?.toLowerCase() || "";
				const username = post.userId?.username?.toLowerCase() || "";
				return caption.includes(q) || username.includes(q);
			});
			if (keywordMatches.length > 0) {
				result = keywordMatches;
			}
		}

		return result;
	}, [posts, selectedTag, isSearching, searchQuery]);

	return (
		<div className="App">
			<div className="MainContainer">
				<ScrollToTop />
				{isMobile ? null : <Header />}
				<div className={`${styles.Container} ${styles.Explore}`}>
					<div className={styles.TopRow}>
						<div className={styles.BackButton}>
							<button onClick={() => navigate("/Home")}>
								<FaAngleLeft />
							</button>
						</div>
						<SmallHeader hideNotification={true} hideSearch={true} />
					</div>

					<div className={styles.ExploreHeader}>
						<div className={styles.HeadingPart}>
							<h1>Explore your World</h1>
						</div>
						{isMobile ? null : (
							<div className={styles.SearchPart}>
								<div className={styles.InputContainer}>
									<input
										type="text"
										placeholder="Search"
										className={showSearchRight ? styles.active : ""}
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										onKeyDown={handleSearch}
									/>
									<img
										src={search}
										alt="search"
										onClick={() => {
											if (showSearchRight && searchQuery.trim() !== "") {
												performSearch();
											} else {
												setShowSearchRight(!showSearchRight);
											}
										}}
										style={{ cursor: "pointer" }}
									/>
								</div>
							</div>
						)}
					</div>

					{/* Active Tag Filter Indicator */}
					{selectedTag && (
						<div className={styles.activeTagBar}>
							<span>
								Showing posts for <strong>#{selectedTag}</strong> ({displayedPosts.length} posts)
							</span>
							<button type="button" onClick={() => setSelectedTag(null)}>
								Clear filter
							</button>
						</div>
					)}

					<div className={styles.ExploreContainer}>
						<div className={styles.ExploreLeft}>
							{/* User Circles Slider */}
							<div className={styles.ExploreUserRowWrapper}>
								{canScrollUserLeft && (
									<button
										type="button"
										className={`${styles.userRowArrow} ${styles.userRowPrev}`}
										onClick={() => scrollUsers("left")}
										aria-label="Previous creators"
									>
										<FaAngleLeft />
									</button>
								)}

								<div
									ref={userRowRef}
									className={styles.ExploreUserRow}
									onScroll={checkUserScroll}
								>
									{userData?.map((user, index) => (
										<div
											key={user._id || index}
											className={styles.UserCircle}
											onClick={() => {
												const targetId = user._id || user.id;
												if (targetId) {
													setCookies({
														url: `${process.env.REACT_APP_TEMPLATE_BASE_URL}`,
														token,
														personaUserId: targetId,
														currentUserId: userId,
													});
												}
											}}
										>
											<div className={styles.UserImageWrapper}>
												<Avatar
													src={user.ProfilePic || user.personaThumbnail}
													name={
														user.username ||
														user.displayName ||
														user.GetstatedName ||
														user.businessName ||
														"User"
													}
													style={{
														width: "100%",
														height: "100%",
														borderRadius: "50%",
														objectFit: "cover",
														fontSize: "4rem",
													}}
												/>
											</div>
											<span>
												{user.username ||
													user.displayName ||
													user.GetstatedName ||
													user.businessName ||
													"User"}
											</span>
										</div>
									))}
								</div>

								{canScrollUserRight && (
									<button
										type="button"
										className={`${styles.userRowArrow} ${styles.userRowNext}`}
										onClick={() => scrollUsers("right")}
										aria-label="Next creators"
									>
										<FaAngleRight />
									</button>
								)}
							</div>

							{/* Feed Sections with Proper Slider */}
							<ExploreYourWorld
								posts={displayedPosts}
								isLoading={isLoading}
								selectedTag={selectedTag}
								setSelectedTag={setSelectedTag}
							/>

							<ExploreYourMind
								posts={displayedPosts}
								isLoading={isLoading}
							/>
						</div>

						{/* Right Sidebar (Desktop) */}
						{!isMobile && (
							<div className={styles.ExploreRight}>
								<div className={styles.TagContainer}>
									<p>Tags For you</p>
									<div className={styles.btnContainer}>
										{TAGS.map((tag) => {
											const isActive = selectedTag === tag;
											return (
												<button
													key={tag}
													type="button"
													style={{
														background: isActive ? "#3A0DBB" : "",
														color: isActive ? "#fff" : "",
													}}
													onClick={() => handleTagToggle(tag)}
												>
													{tag}
												</button>
											);
										})}
									</div>
								</div>

								<div className={styles.ScrollCont}>
									<div className={styles.AddContainer}>
										<img src={AddImage} alt="Promotion" />
									</div>

									<div className={styles.LikeContainer}>
										<p>You may like</p>
										<div className={styles.LikeImages}>
											{displayedPosts.slice(0, 6).map((item, index) => (
												<ExplorePostCard
													key={item._id || index}
													post={item}
													variant="compact"
												/>
											))}
										</div>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default Explore;
